import React, { useState, useMemo, useRef, useEffect } from "react";
// REMOVED: import Table from "react-bootstrap/Table"; (Not strictly needed if you use standard <table> tags with styles, but kept if you prefer bootstrap styling)
import { ChevronDown, X } from "lucide-react"; // Removed unused ChevronLeft/Right
import { getAllAuditLogs } from "../../requests";
import { notifyError } from "../helper/Toaster";
import { startLoader, stopLoader } from '../../_store/reducers/loaderReducer.js'
import { useDispatch } from "react-redux";
import styles from "../../_css/SecurityCompliance.module.css";
import ResponseLoader from "../common/ResponseLoader.js";

// FIX 1: Import the Custom Pagination Component, not the React-Bootstrap one
import Pagination from "../common/Pagination";

const AuditTable = () => {
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        action: '',
        module: '',
        month: ''
    });
    const [fullData, setFullData] = useState([]);

    // Dropdown visibility states
    const [showModuleDropdown, setShowModuleDropdown] = useState(false);
    const [showActionDropdown, setShowActionDropdown] = useState(false);
    const [showMonthDropdown, setShowMonthDropdown] = useState(false);

    // FIX 2: Simplified State (Removed redundant total/totalRecords duplication)
    const [apiResponse, setApiResponse] = useState({
        data: [],
        totalRecords: 0,
        totalPages: 0
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const dispatch = useDispatch();
    const moduleDropdownRef = useRef(null);
    const actionDropdownRef = useRef(null);
    const monthDropdownRef = useRef(null);

    const rowsPerPage = 10;

    // Fetch full data for dropdown options
    useEffect(() => {
        const fetchAll = async () => {
            const params = {
                page,
                limit: rowsPerPage,
                action: filters.action || undefined,
                module: filters.module || undefined,
            };
            const response = await getAllAuditLogs(params);
            setFullData(response.data || []);
        };
        fetchAll();
    }, []);

    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (moduleDropdownRef.current && !moduleDropdownRef.current.contains(event.target)) setShowModuleDropdown(false);
            if (actionDropdownRef.current && !actionDropdownRef.current.contains(event.target)) setShowActionDropdown(false);
            if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) setShowMonthDropdown(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Fetch data when page or filters change
    useEffect(() => {
        fetchAuditData();
    }, [page, filters]);

    const fetchAuditData = async () => {
        setLoading(true);
        setError(null);
        dispatch(startLoader());
        try {
            const params = {
                page,
                limit: rowsPerPage,
                action: filters.action || undefined,
                module: filters.module || undefined,
            };

            const response = await getAllAuditLogs(params);

            if (response?.success) {
                setApiResponse({
                    data: response.data || [],
                    // Ensure your API returns these exact field names
                    totalRecords: response.totalRecords || 0,
                    totalPages: response.totalPages || 0
                });
            } else {
                throw new Error(response?.message || 'Failed to fetch data');
            }
        } catch (error) {
            setError(error.message);
            notifyError('Failed to load audit data');
            setApiResponse({ data: [], totalRecords: 0, totalPages: 0 });
            console.error('Error fetching audit data:', error);
        } finally {
            setLoading(false);
            dispatch(stopLoader())
        }
    };

    const filterOptions = useMemo(() => {
        const actualData = fullData || [];
        const actions = [...new Set(actualData.map(item => item.action))].filter(Boolean);
        const modules = [...new Set(actualData.map(item => item.module))].filter(Boolean);
        return { actions, modules, months: monthNames };
    }, [fullData, monthNames]);

    const filteredData = useMemo(() => {
        if (!filters.month) return apiResponse.data;

        return apiResponse.data.filter(item => {
            if (!item.timestamp) return false;
            const itemDate = new Date(item.timestamp);
            const itemMonth = itemDate.toLocaleDateString('en-US', { month: 'long' });
            return itemMonth === filters.month;
        });
    }, [apiResponse.data, filters.month]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({ ...prev, [filterType]: value }));
        setPage(1);
    };

    const clearAllFilters = () => {
        setFilters({ action: '', module: '', month: '' });
        setPage(1);
        setShowModuleDropdown(false);
        setShowActionDropdown(false);
        setShowMonthDropdown(false);
    };

    const toggleModuleDropdown = () => {
        setShowModuleDropdown(!showModuleDropdown);
        setShowActionDropdown(false);
        setShowMonthDropdown(false);
    };

    const toggleActionDropdown = () => {
        setShowActionDropdown(!showActionDropdown);
        setShowModuleDropdown(false);
        setShowMonthDropdown(false);
    };

    const toggleMonthDropdown = () => {
        setShowMonthDropdown(!showMonthDropdown);
        setShowModuleDropdown(false);
        setShowActionDropdown(false);
    };

    const hasActiveFilters = Object.values(filters).some(filter => filter !== '');
    const displayData = filters.month ? filteredData : apiResponse.data;

    const FilterContainer = () => (
        <div className={styles.filterContainer}>
            <div className={styles.filterButtons}>
                {/* Module Dropdown */}
                <div className={styles.dropdownContainer} ref={moduleDropdownRef}>
                    <button
                        onClick={toggleModuleDropdown}
                        className={styles.filterButton}
                        style={{
                            background: filters.module ? '#E0F7FA' : "#FFFFFF",
                            color: filters.module ? '#00B3B3' : "#6b7280",
                            borderColor: filters.module ? '#00B3B3' : '#D1D5DB'
                        }}
                    >
                        {filters.module || "Module"}
                        <ChevronDown size={16} color={filters.module ? '#00B3B3' : "#6b7280"} style={{ transform: showModuleDropdown ? 'rotate(180deg)' : 'none' }} />
                    </button>
                    {showModuleDropdown && (
                        <div className={styles.dropdownMenu}>
                            <div className={styles.dropdownItem} onClick={() => { handleFilterChange("module", ""); setShowModuleDropdown(false); }}>All Modules</div>
                            {filterOptions.modules.map((module) => (
                                <div key={module} className={styles.dropdownItem} onClick={() => { handleFilterChange("module", module); setShowModuleDropdown(false); }}>{module}</div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Action Dropdown */}
                <div className={styles.dropdownContainer} ref={actionDropdownRef}>
                    <button
                        onClick={toggleActionDropdown}
                        className={styles.filterButton}
                        style={{
                            background: filters.action ? '#E0F7FA' : "#FFFFFF",
                            color: filters.action ? '#00B3B3' : "#6b7280",
                            borderColor: filters.action ? '#00B3B3' : '#D1D5DB'
                        }}
                    >
                        {filters.action || "Action Type"}
                        <ChevronDown size={16} color={filters.action ? '#00B3B3' : "#6b7280"} style={{ transform: showActionDropdown ? 'rotate(180deg)' : 'none' }} />
                    </button>
                    {showActionDropdown && (
                        <div className={styles.dropdownMenu}>
                            <div className={styles.dropdownItem} onClick={() => { handleFilterChange("action", ""); setShowActionDropdown(false); }}>All Actions</div>
                            {filterOptions.actions.map((action) => (
                                <div key={action} className={styles.dropdownItem} onClick={() => { handleFilterChange("action", action); setShowActionDropdown(false); }}>{action}</div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Month Dropdown */}
                <div className={styles.dropdownContainer} ref={monthDropdownRef}>
                    <button
                        onClick={toggleMonthDropdown}
                        className={styles.filterButton}
                        style={{
                            background: filters.month ? '#E0F7FA' : "#FFFFFF",
                            color: filters.month ? '#00B3B3' : "#6b7280",
                            borderColor: filters.month ? '#00B3B3' : '#D1D5DB'
                        }}
                    >
                        {filters.month || "This Month"}
                        <ChevronDown size={16} color={filters.month ? '#00B3B3' : "#6b7280"} style={{ transform: showMonthDropdown ? 'rotate(180deg)' : 'none' }} />
                    </button>
                    {showMonthDropdown && (
                        <div className={styles.dropdownMenu} style={{ maxHeight: '160px', overflowY: 'auto' }}>
                            <div className={styles.dropdownItem} onClick={() => { handleFilterChange("month", ""); setShowMonthDropdown(false); }}>All Months</div>
                            {filterOptions.months.map((month) => (
                                <div key={month} className={styles.dropdownItem} onClick={() => { handleFilterChange("month", month); setShowMonthDropdown(false); }}>{month}</div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                    <button onClick={clearAllFilters} className={styles.clearFilterButton}>
                        Clear Filters <X size={14} />
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className={styles.container}>
            <FilterContainer />

            <div className={styles.tableContainer} style={{ position: 'relative', minHeight: '200px' }}>
                <table className={`${styles.table} text-center align-middle`} style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                        {loading && (
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10, borderRadius: '8px' }}>
                                <ResponseLoader />
                            </div>
                        )}
                        <tr>
                            <th className={`${styles.tableHeader} ${styles.tableHeaderFirst}`}>
                                <div className="d-flex align-items-center">
                                    <input type="checkbox" className="form-check-input me-2" />
                                    <span>S.L</span>
                                </div>
                            </th>
                            {[
                                { label: 'User', width: "200px" },
                                { label: 'Action', width: "140px" },
                                { label: 'Module', width: "140px" },
                                { label: 'Timestamp', width: "200px" },
                                { label: 'Before', width: "100px" },
                                { label: 'After', width: "100px" }
                            ].map((col, idx, array) => (
                                <th key={idx} className={styles.tableHeader} style={idx === array.length - 1 ? { borderTopRightRadius: "8px" } : {}}>
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && displayData.length > 0 ? (
                            displayData.map((row, index) => {
                                const isLastRow = index === displayData.length - 1;
                                const serialNumber = (page - 1) * rowsPerPage + index + 1;

                                return (
                                    <tr key={index}>
                                        <td className={`${styles.tableCell} ${styles.tableCellFirst}`} style={isLastRow ? { borderBottom: '1px solid #E5E7EB !important', borderBottomLeftRadius: '8px !important' } : {}}>
                                            <div className="d-flex align-items-center">
                                                <input type="checkbox" className="form-check-input me-3" />
                                                <span style={{ color: '#6B7280', fontWeight: '500', fontSize: '14px' }}>{String(serialNumber).padStart(2, '0')}</span>
                                            </div>
                                        </td>
                                        <td className={`${styles.tableCell}`}>
                                            <span className="d-flex align-items-center">
                                                <span className="me-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#e9ecef', overflow: 'hidden', borderRadius: '8px' }}>
                                                    {row.user?.profile ? (
                                                        <img src={row.user.profile} alt={row.user?.name} width="28" height="28" style={{ objectFit: "cover" }} />
                                                    ) : (
                                                        <span style={{ fontSize: '16px', color: '#6c757d' }}>👤</span>
                                                    )}
                                                </span>
                                                <span style={{ color: '#6B7280', fontWeight: '500', fontSize: '16px' }}>{row.user?.name || "N/A"}</span>
                                            </span>
                                        </td>
                                        <td className={`${styles.tableCell}`}>{row.action}</td>
                                        <td className={`${styles.tableCell}`}>{row.module}</td>
                                        <td className={`${styles.tableCell}`}>{row.timestamp}</td>
                                        <td className={`${styles.tableCell}`}>{row.before || "N/A"}</td>
                                        <td className={`${styles.tableCell} ${styles.tableCellLast}`} style={isLastRow ? { borderBottom: '1px solid #E5E7EB !important', borderBottomRightRadius: '8px !important' } : {}}>
                                            {row.after === 0 ? 0 : row.after || "N/A"}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            !loading && (
                                <tr>
                                    <td colSpan="7" className={styles.emptyState}>No data to preview</td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
            </div>

            {/* FIX 3: Implementation of your Custom Pagination Component */}
            {!loading && !error && apiResponse.totalPages > 1 && (
                <Pagination
                    currentPage={page}
                    totalCount={apiResponse.totalRecords}
                    pageSize={rowsPerPage}
                    onPageChange={handlePageChange}
                />
            )}
        </div>
    );
};

export default AuditTable;